import * as Array from 'effect/Array';
import * as Data from 'effect/Data';
import * as Either from 'effect/Either';
import { dual } from 'effect/Function';
import * as Predicate from 'effect/Predicate';

/**
 * `ParseIssue` is a type that represents the different types of errors that can occur when decoding/encoding a value.
 */
export type ParseIssue =
  | Composite
  | Refinement
  | Type
  | Transformation
  | Missing
  | Unexpected;

/**
 * Error that contains multiple issues.
 *
 * @category model
 */
export class Composite {
  readonly _tag = '@fmnts/io/parse-issue/composite';
  constructor(
    readonly actual: unknown,
    readonly issues: Array.NonEmptyReadonlyArray<ParseIssue>,
    readonly output?: unknown,
  ) {}
}
/**
 * Error that occurs when a refinement has an error.
 *
 * @category model
 */
export class Refinement {
  readonly _tag = '@fmnts/io/parse-issue/refinement';
  constructor(
    readonly actual: unknown,
    readonly kind: 'From' | 'Predicate',
    readonly issue: ParseIssue,
  ) {}
}

/**
 * Error that occurs when a transformation has an error.
 *
 * @category model
 */
export class Transformation {
  readonly _tag = '@fmnts/io/parse-issue/transformation';
  constructor(
    readonly actual: unknown,
    readonly message?: string,
  ) {}
}

/**
 * The `Type` variant of the `ParseIssue` type represents an error that
 * occurs when the `actual` value is not of the expected type.
 * The `actual` field contains the value that caused the error.
 *
 * @category model
 */
export class Type {
  readonly _tag = '@fmnts/io/parse-issue/type';
  constructor(
    readonly actual: unknown,
    readonly message?: string,
  ) {}
}

/**
 * Error that occurs when an unexpected key or index is present.
 *
 * @category model
 */
export class Unexpected {
  readonly _tag = '@fmnts/io/parse-issue/unexpected';
  constructor(
    readonly actual: unknown,
    readonly message?: string,
  ) {}
}
/**
 * Error that occurs when a required key or index is missing.
 *
 * @category model
 */
export class Missing {
  readonly _tag = '@fmnts/io/parse-issue/missing';
  constructor(
    readonly actual: unknown,
    readonly message?: string,
  ) {}
}

/**
 * @category type id
 */
const ParseErrorTypeId: unique symbol = Symbol.for(
  '@fmnts/io/ParseErrorTypeId',
);
type ParseErrorTypeId = typeof ParseErrorTypeId;

/**
 * Represents an error encountered while encoding/decoding
 * via a codec.
 *
 * @category model
 */
export class ParseError extends Data.TaggedError('@fmnts/io/ParseError')<{
  readonly issue: ParseIssue;
}> {
  readonly [ParseErrorTypeId] = ParseErrorTypeId;
}

/**
 * Creates a `ParseError` from a `ParseIssue`.
 *
 * @param issue Issue leading to the error.
 *
 * @category constructors
 */
export const parseError = (issue: ParseIssue): ParseError =>
  new ParseError({ issue });

/**
 * @category guards
 */
export const isParseError = (u: unknown): u is ParseError =>
  Predicate.hasProperty(u, ParseErrorTypeId);

/**
 * @category constructors
 */
export const succeed: <A>(a: A) => Either.Either<A, ParseIssue> = Either.right;

/**
 * @category constructors
 */
export const fail: (issue: ParseIssue) => Either.Either<never, ParseIssue> =
  Either.left;

export const mapBoth: {
  <E, E2, A, A2>(options: {
    readonly onFailure: (e: E) => E2;
    readonly onSuccess: (a: A) => A2;
  }): (self: Either.Either<A, E>) => Either.Either<A2, E2>;
  <A, E, E2, A2>(
    self: Either.Either<A, E>,
    options: {
      readonly onFailure: (e: E) => E2;
      readonly onSuccess: (a: A) => A2;
    },
  ): Either.Either<A2, E2>;
} = dual(
  2,
  <A, E, E2, A2>(
    self: Either.Either<A, E>,
    options: {
      readonly onFailure: (e: E) => E2;
      readonly onSuccess: (a: A) => A2;
    },
  ): Either.Either<A2, E2> =>
    Either.mapBoth(self, {
      onLeft: options.onFailure,
      onRight: options.onSuccess,
    }),
);
